import { Button, CloseIcon, LogoutIcon, SideBar, SideBarTriggerButton, useDomain } from '@netfront/ui-library';
import IconMenu from '../Icons/IconMenu';
import React from 'react';
import { useIsMounted } from '@netfront/common-library';
import { cn } from '@/utils';
import UserAvatar from '../UserMenu/UserAvatar';
import { useUserContext } from '@/contexts/UserContext';
import SkeletonLoader from '../SkeletonLoader/SkeletonLoader';
import { CONSTANTS } from '@/constants';
import Link from 'next/link';
import { useLogout } from '@netfront/gelada-identity-library';
import { useRouter } from 'next/router';
import { Logo } from '../Logo/Logo';
import { useSmallScreenNavigationSideMenu } from './SmallScreenNavigationSideMenuProvider';

export const SmallScreenNavigationSideMenu = ({ triggerIconClass }: { triggerIconClass?: string }) => {
  const { isMounted } = useIsMounted();
  const { isSidebarOpen: sidebarOpen, toggleSidebar } = useSmallScreenNavigationSideMenu();
  const { user } = useUserContext();

  const { push } = useRouter();
  const { getDomain } = useDomain();
  const { handleLogout } = useLogout({
    onCompleted: () => push('/'),
  });

  const currentYearAsString = React.useMemo(() => {
    return new Date().getFullYear().toString();
  }, []);

  return (
    isMounted && (
      <div>
        <SideBarTriggerButton
          isSideBarOpen={sidebarOpen}
          onClick={() => toggleSidebar(true)}
          buttonContent={{
            icon: (props) => <IconMenu className={cn(triggerIconClass, props.className)} />,
          }}
        />
        <SideBar isSideBarOpen={sidebarOpen} onClose={() => toggleSidebar(false)}>
          <div className="p-4 flex flex-col gap-4 h-full">
            <div className="mb-12">
              <SideBarTriggerButton
                isSideBarOpen={sidebarOpen}
                onClick={() => toggleSidebar(false)}
                buttonContent={{
                  icon: (props) => <CloseIcon className={cn('c-sidebar-close-trigger', props.className)} />,
                }}
              />
            </div>

            {user ? (
              <>
                <UserAvatar size={'large'} />
                <header className="text-lg">
                  <p className="text-primary-foreground h5">Welcome Back,</p>
                  {user ? (
                    <p className="text-primary-foreground h2">{user?.firstName}</p>
                  ) : (
                    <SkeletonLoader additionalClassNames="h-[1lh] w-8" />
                  )}
                </header>
                <ul className="list-style-none py-8">
                  {CONSTANTS.MENU.PRIVATE.map(({ href, id, label }) => (
                    <li className="flex flex-col gap-4 m-0 mb-8" key={id}>
                      <Link id={id} href={href} className="text-decoration-none text-primary-foreground text-lg">
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
                <div className="flex-1 flex flex-col justify-end items-start">
                  <Button
                    additionalClassNames="mb-4"
                    text="Logout"
                    icon={LogoutIcon}
                    variant="primary"
                    onClick={() => handleLogout({ domain: getDomain(), path: '/' })}
                  />
                  <p className="text-primary-foreground">© {currentYearAsString} DAP Mental Wellbeing Portal</p>
                </div>
              </>
            ) : (
              <>
                <Logo additionalClassNames="w-72 max-w-full" />
                <ul className="list-style-none py-8">
                  {CONSTANTS.MENU.PUBLIC.map(({ href, id, label }) => (
                    <li className="flex flex-col gap-4 m-0 mb-8" key={id}>
                      <Link id={id} href={href} className="text-decoration-none text-primary-foreground text-lg">
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>

                <div aria-label="Login and registration" className="flex gap-4 mt-auto mx-auto py-4">
                  <Button linkButton={{ id: 'register', url: '/register' }} text="Register" variant="accent" />
                  <Button linkButton={{ id: 'login', url: '/login' }} text="Login" variant="secondary" />
                </div>
              </>
            )}
          </div>
        </SideBar>
      </div>
    )
  );
};

export default SmallScreenNavigationSideMenu;
